import React from 'react';
import { Grid, Typography, Paper, Box } from '@mui/material';
import WorkflowNodeEvent from './WorkflowNodeEvent';
import WorkflowNodeCondition from './WorkflowNodeCondition';
import WorkflowNodeAction from './WorkflowNodeAction';
import { useWorkflowStyles } from './styles';
import { VOStatus, WorkflowEventType } from './constants';
import { CreditLineStatusEnum } from '@trnsact/trnsact-shared-types';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

interface WorkflowDiagramProps {
  selectedEvent: WorkflowEventType;
  selectedConditions: (VOStatus | CreditLineStatusEnum)[];
  selectedActionConfiguration: any;
  onEventClick: () => void;
  onConditionClick: () => void;
  onActionClick: () => void;
  onDeleteCondition: (index: number) => void;
  drawerContainerRef: React.RefObject<HTMLDivElement>;
}

const WorkflowDiagram: React.FC<WorkflowDiagramProps> = ({
  selectedEvent,
  selectedConditions,
  selectedActionConfiguration,
  onEventClick,
  onConditionClick,
  onActionClick,
  onDeleteCondition,
  drawerContainerRef,
}) => {
  const classes = useWorkflowStyles();

  const FlowArrow = () => (
    <Grid item xs={8} style={{ textAlign: 'center', margin: '8px 0' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <Box
          sx={{
            width: 2,
            height: 24,
            backgroundColor: '#90cbf9',
          }}
        />
        <ArrowDownwardIcon
          sx={{
            color: '#90cbf9',
            fontSize: 28,
            marginTop: '-8px',
          }}
        />
      </Box>
    </Grid>
  );

  const StartNode = () => (
    <Grid item xs={8} style={{ textAlign: 'center', margin: '16px 0 8px 0' }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          padding: '8px 20px',
          backgroundColor: '#4caf50',
          color: 'white',
          borderRadius: '24px',
          fontWeight: 600,
          fontSize: '14px',
          boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
        }}
      >
        <PlayCircleOutlineIcon sx={{ fontSize: 20 }} />
        START
      </Box>
    </Grid>
  );

  const EndNode = () => (
    <Grid item xs={8} style={{ textAlign: 'center', margin: '8px 0 16px 0' }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          padding: '8px 20px',
          backgroundColor: '#2196f3',
          color: 'white',
          borderRadius: '24px',
          fontWeight: 600,
          fontSize: '14px',
          boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)',
        }}
      >
        <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
        END
      </Box>
    </Grid>
  );

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h5">Diagram</Typography>
      </Grid>
      <Grid item xs={12}>
        <Paper
          className={classes.paperContainer}
          sx={{
            maxHeight: 'calc(100vh - 250px)',
            overflowY: 'auto',
            overflowX: 'hidden',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#90cbf9',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#2196F3',
            },
          }}
        >
          <Box ref={drawerContainerRef} sx={{ minHeight: '100%', pb: 2 }}>
            <Grid container justifyContent="center" spacing={0}>
              <StartNode />

              <FlowArrow />

              <WorkflowNodeEvent selectedEvent={selectedEvent} onClick={onEventClick} />

              <FlowArrow />

              <WorkflowNodeCondition
                selectedConditions={selectedConditions}
                selectedEvent={selectedEvent}
                onClick={onConditionClick}
                onDeleteCondition={onDeleteCondition}
              />

              <FlowArrow />

              <WorkflowNodeAction selectedActionConfiguration={selectedActionConfiguration} onClick={onActionClick} />

              <FlowArrow />

              <EndNode />
            </Grid>
          </Box>
        </Paper>
      </Grid>
    </>
  );
};

export default WorkflowDiagram;
